import React, {Component, ReactElement, ReactNode} from 'react'
import {Nullable} from '../../../../app/TypeHelpers';
import {ReservationOption, ReserveFacilityModel} from '../../../../components/reservations/ReserveFacilityModel';
import {FacilityOption} from '../../../../components/reservations/ResidentReserveFacilityFormComponents/RRFFReserveOptionInput';
import {
    CASH_PAYMENT_STATUS,
    FACILITY_RESERVATION_CLASSIFICATION,
    PAYMENT_TYPE,
    VALID_OR_NOT,
} from '../../../../CodeMaster';
import {NoSelectedValue} from '../../../../componentHelper/selectHelper';
import {SortableTableHeaderCell} from '../../../../styledComponents/adminPage/SortablePaginationTableHeader';
import {ConditionTable} from '../../../../styledComponents/adminPage/ConditionTable';
import {StyledTableCaption, WrapTableRowName} from '../../../../styledComponents/adminPage/Table';
import {ColumnFlexContainer, LeftRowFlexContainer} from '../../../../styledComponents/FlexComponents';
import PPSelect, {OptionProps} from '../../../../components/shared/forms/PPSelect';
import styled from 'styled-components';
import { Table, TableBody, TableHead, TableRow, TableCell, Box } from '@mui/material';
import {StyledDiv} from '../../../../styledComponents/StyledTypeDefinitions';
import EventEmitterExAdmin from '../../../../components/shared/pageBaseComponents/EventEmitterExAdmin';
import PPRadioGroup, {PPRadioItemProp} from '../../../../components/shared/forms/PPRadioGroup';
import {NatFacilityOptionDetailInfo} from './NatAdminFacilityReserve';
import {SelectChangeEvent} from '@mui/material/Select'

interface Props {
    reservationModel: ReserveFacilityModel
    facilityOptions: FacilityOption[]
    facilityOptionsDetailInfo: NatFacilityOptionDetailInfo[]
    reservationOptions: ReservationOption[]
    facilityReserveCategory: string
    eventEmitter: EventEmitterExAdmin
    onChangeReservationOptions: (reservationOptions: ReservationOption[]) => void
    onChangeFacilityOptionDetailInfo: (facilityOptionsDetailInfo: NatFacilityOptionDetailInfo[]) => void
}

interface States {
    facilityOptionsDetailInfo: NatFacilityOptionDetailInfo[]
    reservationOptions: ReservationOption[]
}


class NatAdminFacilityReserveOptionComponent extends Component<Props, States> {
    constructor(props: Props) {
        super(props);

        this.state = {
            facilityOptionsDetailInfo: props.facilityOptionsDetailInfo,
            reservationOptions: props.reservationOptions,
        };

        this.handleChangeOptionSelect = this.handleChangeOptionSelect.bind(this);
        this.handleChangeOptionQuantity = this.handleChangeOptionQuantity.bind(this);
        this.handleChangeOptionPaymentMethod = this.handleChangeOptionPaymentMethod.bind(this);
    }

    async componentDidMount(): Promise<void> {
        const {facilityOptions, reservationOptions} = this.props;

        const initialReservationOptions = facilityOptions.map(facilityOption => {
                const quantity: number = facilityOption.required === VALID_OR_NOT.VALID
                    ? facilityOption.minimumQuantity
                    : 0;
                return {
                    optionId: facilityOption.optionId,
                    quantity: quantity,
                    sortOrder: facilityOption.sortOrder,
                }
            },
        ).filter(option => option.quantity > 0,
        ).sort((a, b) => a.sortOrder - b.sortOrder);

        const nextReservationOptions =
            (reservationOptions.length > 0)
                ? reservationOptions
                : initialReservationOptions;


        this.setState({
            reservationOptions: nextReservationOptions,
        }, () => {
            this.updateModel()
        })
    }

    getQuantityList(min: number, max: number): OptionProps[] {
        const quantityList: OptionProps[] = [];
        for (let i: number = min; i <= max; i++) {
            quantityList.push({
                label: String(i),
                value: String(i),
            })
        }

        return [{
            label: '選択してください',
            value: NoSelectedValue,
        }, ...quantityList];
    }

    getPaymentMethodList(): OptionProps[] {
        let paymentTypes: OptionProps[];

        paymentTypes = PAYMENT_TYPE.values().map((paymentType) => {
            return ({
                label: `${paymentType.label}`,
                value: `${paymentType.value}`,
            })
        });

        return paymentTypes
    }

    getCashPaymentStatusRadioGroupData(targetOptionId: number): PPRadioItemProp[] {
        const facilityOption =
            this.state.facilityOptionsDetailInfo.find(option => option.optionId === targetOptionId);

        let defaultCashPaymentStatus: string = CASH_PAYMENT_STATUS.YET_PAID.value;
        if (facilityOption && facilityOption.cashPaymentStatus) {
            defaultCashPaymentStatus = facilityOption.cashPaymentStatus
        }

        const cashPaymentStatusData: PPRadioItemProp[] = [];
        CASH_PAYMENT_STATUS.values().map((cashPaymentStatus) => (
            cashPaymentStatusData.push(
                {
                    label: cashPaymentStatus.label,
                    value: cashPaymentStatus.value,
                    radioProps: {
                        checked: (facilityOption) ?
                            defaultCashPaymentStatus === cashPaymentStatus.value : false,
                        onChange: this.handleChangeOptionCashPaymentStatus(targetOptionId),
                    },
                },
            )
        ));
        return cashPaymentStatusData
    }

    updateModel(): void {
        const reservationOptions = this.state.reservationOptions
            .filter(option => option.quantity > 0)
            .map(option => {
                    return {
                        optionId: option.optionId,
                        quantity: option.quantity,
                        sortOrder: option.sortOrder,
                    }
                },
            )
            .sort((a, b) => a.sortOrder - b.sortOrder);
        this.props.reservationModel.withReservationOptions(reservationOptions)
    }

    handleChangeOptionQuantity = (targetOptionId: number) => (event: SelectChangeEvent<unknown>) => {
        const changedOptionValue = event.target.value === NoSelectedValue ? 0 : Number(event.target.value);
        const facilityOption = this.props.facilityOptions.find(option => option.optionId === targetOptionId);

        const reservationOptions = this.state.reservationOptions;
        if (facilityOption && reservationOptions) {
            const changedOptionIndex =
                reservationOptions.findIndex(option => (option.optionId === targetOptionId));
            if (changedOptionValue === 0) {
                reservationOptions.splice(changedOptionIndex, 1);
            } else {
                if (changedOptionIndex === -1) {
                    reservationOptions.push({
                        optionId: facilityOption.optionId,
                        quantity: changedOptionValue,
                        sortOrder: facilityOption.sortOrder,
                    })
                } else {
                    reservationOptions[changedOptionIndex].quantity = changedOptionValue
                }
            }
        }

        reservationOptions.sort((a, b) => a.sortOrder - b.sortOrder).sort((a, b) => a.optionId - b.optionId)
        this.setState({reservationOptions: reservationOptions}, () => {
            this.updateModel();
            this.props.onChangeReservationOptions(reservationOptions);
        })
    };

    handleChangeOptionSelect(changedOptionId: string): void {
        const selectOptionId = Number(changedOptionId);
        const changedOptionValue = changedOptionId === NoSelectedValue ? 0 : Number(selectOptionId);
        const facilityOption = this.props.facilityOptions.find(option => option.optionId === selectOptionId);

        let reservationOptions: ReservationOption[] = this.state.reservationOptions;

        if (facilityOption) {
            if (reservationOptions) {
                reservationOptions.push({
                    optionId: facilityOption.optionId,
                    quantity: changedOptionValue,
                    sortOrder: facilityOption.sortOrder,
                })
            } else {
                reservationOptions = [
                    {
                        optionId: facilityOption.optionId,
                        quantity: changedOptionValue,
                        sortOrder: facilityOption.sortOrder,
                    }]
            }
        }
        if (reservationOptions !== null) {
            this.setState({reservationOptions: reservationOptions}, () => {
                this.updateModel();
                this.props.onChangeReservationOptions(reservationOptions);
            })
        }

    }

    handleChangeOptionPaymentMethod = (targetOptionId: number) => (event: SelectChangeEvent<unknown>) => {
        const changedOptionValue = event.target.value === NoSelectedValue ? '0' : String(event.target.value);


        const facilityDetailInfo = this.state.facilityOptionsDetailInfo;
        if (facilityDetailInfo) {
            const changedOptionIndex =
                facilityDetailInfo.findIndex(option => (option.optionId === targetOptionId));
            if (changedOptionIndex === -1) {
                return
            } else {
                facilityDetailInfo[changedOptionIndex].paymentType =
                    PAYMENT_TYPE.codeToEnumConstants(changedOptionValue);

                if (facilityDetailInfo[changedOptionIndex].paymentType === PAYMENT_TYPE.CASH) {
                    facilityDetailInfo[changedOptionIndex].cashPaymentStatus = CASH_PAYMENT_STATUS.YET_PAID.value
                }
                this.setState({facilityOptionsDetailInfo: facilityDetailInfo}, () => {
                    this.updateModel();
                    this.props.onChangeFacilityOptionDetailInfo(facilityDetailInfo);
                })
            }
        }
    };

    handleChangeOptionCashPaymentStatus = (targetOptionId: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
        const changedOptionValue = event.target.value === NoSelectedValue ? '0' : String(event.target.value);

        const facilityDetailInfo = this.state.facilityOptionsDetailInfo;
        if (facilityDetailInfo) {
            const changedOptionIndex =
                facilityDetailInfo.findIndex(option => (option.optionId === targetOptionId));
            if (changedOptionIndex === -1) {
                return
            } else {
                facilityDetailInfo[changedOptionIndex].cashPaymentStatus = changedOptionValue;
                this.setState({facilityOptionsDetailInfo: facilityDetailInfo}, () => {
                    this.updateModel();
                    this.props.onChangeFacilityOptionDetailInfo(facilityDetailInfo);
                })
            }
        }
    };

    renderOptionFormTable(): Nullable<ReactNode> {
        const {facilityOptions, reservationOptions, facilityOptionsDetailInfo, facilityReserveCategory} = this.props;

        if (facilityOptions.length === 0) {
            return null
        }

        let headers: SortableTableHeaderCell[] = [];

        if (this.props.facilityReserveCategory === FACILITY_RESERVATION_CLASSIFICATION.ADMIN.value) {
            headers = [
                {id: 'option', label: 'オプション'},
                {id: 'charge', label: '料金'},
                {id: 'quantity', label: '数量'},
            ];
        } else {
            headers = [
                {id: 'option', label: 'オプション'},
                {id: 'charge', label: '料金'},
                {id: 'quantity', label: '数量'},
                {id: 'paymentType', label: '支払方法'},
            ];
        }

        return (
            <>
                <ConditionTable>
                    <StyledTableCaption>オプション選択</StyledTableCaption>
                </ConditionTable>
                <StyledTable stickyHeader>
                    <colgroup>
                        {(facilityReserveCategory !==
                            FACILITY_RESERVATION_CLASSIFICATION.ADMIN.value) ?
                            <>
                                <col width={'21%'}/>
                                <col width={'18%'}/>
                                <col width={'31%'}/>
                                <col width={'35%'}/>
                            </> :
                            <>
                                <col width={'15%'}/>
                                <col width={'25%'}/>
                                <col width={'35%'}/>
                            </>
                        }
                    </colgroup>
                    <TableHead>
                        <StyledTableRow>
                            {headers.map((header) => {
                                return (
                                    <StyledTableCell key={header.id}
                                                     align={'left'}>{header.label}</StyledTableCell>
                                )
                            })}
                        </StyledTableRow>
                    </TableHead>
                    <TableBody>
                        {facilityOptions.map((facilityOption, index) => {
                            // 数量
                            const targetOption: ReservationOption | undefined = (this.props.facilityOptions) ?
                                reservationOptions.find(reservationOption =>
                                    reservationOption.optionId === facilityOption.optionId) : undefined;
                            const defaultValue =
                                (targetOption) ? `${targetOption.quantity}` : NoSelectedValue;
                            const required = facilityOption.required === VALID_OR_NOT.VALID;
                            const outline = facilityOption.outline.split('/');

                            // 支払方法/単位設定
                            const targetOptionDetailInfo: NatFacilityOptionDetailInfo | undefined =
                                facilityOptionsDetailInfo.find(optionDetailInfo =>
                                    optionDetailInfo.optionId === facilityOption.optionId);

                            const viewPaymentMethod =
                                (targetOptionDetailInfo) ? `${targetOptionDetailInfo.paymentType.value}` : '';
                            const viewUnitName =
                                (targetOptionDetailInfo) ? `${targetOptionDetailInfo.unitName}` : '';

                            return (
                                <TableRow key={index}>
                                    <WrapTableRowName align={'left'} required={required}>
                                        {facilityOption.optionName}
                                    </WrapTableRowName>
                                    <TableCell align={'left'}>{outline[0]}</TableCell>
                                    <TableCell align={'left'}>
                                        <LeftRowFlexContainer>
                                            <PPSelect
                                                data={this.getQuantityList(
                                                    facilityOption.minimumQuantity,
                                                    facilityOption.maximumQuantity)}
                                                onChange={
                                                    this.handleChangeOptionQuantity(facilityOption.optionId)}
                                                eventKeys={['volume']}
                                                eventEmitter={this.props.eventEmitter}
                                                value={defaultValue}
                                            />
                                            <QuantityContainer>{viewUnitName}/{outline[1]}</QuantityContainer>
                                        </LeftRowFlexContainer>
                                    </TableCell>
                                    {this.props.facilityReserveCategory !==
                                    FACILITY_RESERVATION_CLASSIFICATION.ADMIN.value &&
                                    <TableCell align={'left'}>
                                        <ColumnFlexContainerStart>
                                            <PPSelect
                                                data={this.getPaymentMethodList()}
                                                onChange={
                                                    this.handleChangeOptionPaymentMethod(
                                                        facilityOption.optionId)}
                                                eventKeys={['paymentMethod']}
                                                eventEmitter={this.props.eventEmitter}
                                                value={viewPaymentMethod}
                                            />
                                            {viewPaymentMethod === PAYMENT_TYPE.CASH.value &&
                                            <CashContainer>
                                                <Box pr={'15px'} pl={'15px'}>現金支払ステータス</Box>
                                                <PPRadioGroup
                                                    row
                                                    data={
                                                        this.getCashPaymentStatusRadioGroupData(
                                                            facilityOption.optionId)}
                                                    eventKeys={['cashPaymentStatus']}
                                                    eventEmitter={this.props.eventEmitter}
                                                />
                                            </CashContainer>
                                            }
                                        </ColumnFlexContainerStart>
                                    </TableCell>
                                    }

                                </TableRow>
                            )
                        })}
                    </TableBody>
                </StyledTable>
            </>
        )
    }

    render(): Nullable<ReactElement> {
        return (
            <>
                {this.renderOptionFormTable()}
            </>
        )
    }
}


export default NatAdminFacilityReserveOptionComponent;

const StyledTable = styled(Table)`
 table-layout: fixed;
`;

const StyledTableRow = styled(TableRow)`
 white-space: nowrap;
`;

const StyledTableCell = styled(TableCell)`
  &.MuiTableCell-root {
   background-color: #ffffff;
  }
`;

const QuantityContainer: StyledDiv = styled.div`
 flex: 1 0 0%;
`;

const ColumnFlexContainerStart = styled(ColumnFlexContainer)`
 align-items: start;
`;

const CashContainer: StyledDiv = styled.div`
 display: flex;
 flex-direction: row;
 align-items: center;
`;
